import { debounce, Grid } from '@mui/material';
import { Location } from '@nx-smartmonkey/shared/domain';
import {
  Checkbox,
  HSpacer,
  RoutalAvatar,
  RoutalAvatarManager,
  RoutalDialog,
  RoutalPalette,
  RoutalSelect,
  RoutalSelectMultiSelectOption,
  VSpacer,
} from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';

import { HeaderContent } from '../../components/HeaderContent';
import * as routing from '../../constants/Routing';
import { Project } from '../../domain/project/Project';
import { CurrencyTypes, DistanceType, VolumeType, WeightType } from '../../domain/project/ProjectDTO';
import { ProjectUnits } from '../../domain/project/ProjectUnits';
import { generateAttachmentName, MAX_AVATAR_IMAGE_SIZE, UPLOAD_TYPES } from '../../helpers/Images';
import { intl } from '../../helpers/IntGlobalProvider';

// import moment from 'moment-timezone';
import { useRetrieveOrganization } from '../../hooks/organization/useRetrieveOrganization';
import { useRetrieveOrganizationResources } from '../../hooks/organization/useRetrieveOrganizationResources';
import useCreateProjectAvatar from '../../hooks/project/useCreateProjectAvatar';
import { useDeleteProject } from '../../hooks/project/useDeleteProject';
import useRemoveProjectAvatar from '../../hooks/project/useDeleteProjectAvatar';
import { useUpdateProject } from '../../hooks/project/useUpdateProject';
import { useUpdateProjectTraffic } from '../../hooks/project/useUpdateProjectTraffic';
import { useAsUser } from '../../hooks/session/useAsUser';
import { useRetrieveUserCredentials } from '../../hooks/user/useRetrieveUserCredentials';
import { ProjectViewMapper } from '../../mappers/project/ProjectViewMapper';
import Button from '../../react-components/Button';
import FormattedMessage from '../../react-components/FormattedCustomMessage';
import InputLocation from '../../react-components/InputLocation';
import InputText from '../../react-components/InputText';
import Text from '../../react-components/Text';
import { TimeZoneSelector } from '../../react-components/TimeZoneSelector';
import { getLocationFromCoordinates, getUploadAsBase64 } from '../../services/requests';
import { ContainerInputs } from './shared';

const settingsCurrency = [
  { value: `EUR` as CurrencyTypes, label: `EUR (€)` },
  { value: `USD` as CurrencyTypes, label: `USD ($)` },
  { value: `MXN` as CurrencyTypes, label: `MXN ($)` },
];
const settingsDistance = [
  { value: `km` as DistanceType, label: `Km` },
  { value: `mi` as DistanceType, label: `Mi` },
];
const settingsWeight = [
  { value: `kg` as WeightType, label: `kg` },
  { value: `lb` as WeightType, label: `lb` },
];
const settingsVolume = [
  { value: `m³` as VolumeType, label: `m³` },
  { value: `u` as VolumeType, label: `u` },
  { value: `l` as VolumeType, label: `l` },
];

type ProjectSettingsGeneralProps = {
  project: Project;
};

export const ProjectSettingsGeneral = ({ project }: ProjectSettingsGeneralProps) => {
  const { data: organization } = useRetrieveOrganization();
  const { data: organizationResources } = useRetrieveOrganizationResources();
  const { run: uploadAvatar } = useCreateProjectAvatar({ project });
  const { run: removeAvatar } = useRemoveProjectAvatar({ project });
  const { data: userCredentials } = useRetrieveUserCredentials();
  const { asUser } = useAsUser();
  const { run: updateProject } = useUpdateProject({ project });
  const { run: deleteProject, success: deleteSuccess } = useDeleteProject();
  const { run: updateTraffic, isLoading: updateTrafficLoading } = useUpdateProjectTraffic();

  const [timezone, setTimezone] = useState<string>((project.time_zone as string) ?? `Europe/Brussels`);
  const [location, setLocation] = useState<Location | undefined | null>(undefined);
  const [locationLabel, setLocationLabel] = useState<string>(``);

  const [units, setUnits] = useState<ProjectUnits | undefined>(undefined);
  const [confirmDeleteOrganization, setConfirmDeleteOrganization] = useState<string>(``);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState<boolean>(false);
  const [trafficDialogVisible, setTrafficDialogVisible] = useState<boolean>(false);

  const canUpdateProject = userCredentials?.projects.test(project.id, `can_update_project`) ?? false;

  useEffect(() => {
    if (deleteSuccess) {
      // WARNING: Without the timeout, the navigate does not work.
      setTimeout(() => {
        window.open(
          `${routing.PROJECT_SETTINGS.ROOT.replace(`:projectId`, organization!.default_project_id)}`,
          `_self`
        );
      });
      setDeleteDialogVisible(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteSuccess]);

  useEffect(() => {
    if (project) {
      setUnits(project?.units ? ProjectUnits.create(project.units.getValue()) : undefined);

      if (project.location) {
        (async () => {
          setLocation(project.location);

          if (project.location?.label && project.location?.label !== ``) {
            setLocationLabel(project.location.label);
          } else if (project.location?.lat && project.location?.lng) {
            const result = await getLocationFromCoordinates([
              { lat: project.location?.lat, lng: project.location?.lng },
            ]);

            let value = ``;
            if (result.length > 0) {
              if (result[0].label) {
                value = result[0].label;
              } else if (result[0].lng && result[0].lat) {
                value = `${result[0].lat}, ${result[0].lng}`;
              }
            } else {
              value = `${project.location?.lat}, ${project.location?.lng}`;
            }

            setLocationLabel(value);
          }
        })();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project]);

  const updateName = debounce((value) => {
    updateProject({ label: value });
  }, 500);
  const updateDescription = debounce((value) => {
    updateProject({ description: value });
  }, 500);

  if (!project) return null;

  const projectResources = organizationResources?.getProjectResources(project.id);
  return (
    <div style={{ padding: `16px 22px` }}>
      <Grid container direction="column">
        <Grid container direction="column" style={{ padding: `16px 22px` }}>
          <Grid container direction="row" wrap="nowrap">
            <HeaderContent titleIntlId="projects.settings.general.title" subtitle="projects.settings.general.article" />

            <Grid item container wrap="nowrap" justifyContent="flex-end" alignItems="center">
              <Grid item>
                <Text
                  variant="body-large"
                  intlId="projects.settings.general.assigned_vehicles"
                  intlValues={{ number: projectResources?.max_vehicles_plan ?? 0 }}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Grid
        container
        direction="column"
        style={{
          padding: `16px 22px`,
        }}
      >
        <Grid item>
          {canUpdateProject ? (
            <RoutalAvatarManager
              id="project_settings_general_image_upload"
              shape="round"
              avatarUrl={project.avatar_url}
              fileName={generateAttachmentName(UPLOAD_TYPES.PROJECT)}
              maxFileSize={MAX_AVATAR_IMAGE_SIZE}
              confirmText={intl.formatMessage({ id: `confirm` })}
              cancelText={intl.formatMessage({ id: `cancel` })}
              onRemoveAvatar={() => removeAvatar()}
              onAvatarUpload={(fileName: string, mimeType: string, file: Blob) =>
                uploadAvatar({ fileName, mimeType, file })
              }
              getUploadAsBase64={getUploadAsBase64}
              width={180}
              height={180}
            />
          ) : (
            <RoutalAvatar
              containerStyles={{ height: `180px`, width: `180px` }}
              url={project.avatar_url}
              getUploadAsBase64={getUploadAsBase64}
            />
          )}
          <ContainerInputs>
            <VSpacer large />

            {canUpdateProject ? (
              <>
                <Text variant="label" intlId="projects.settings.general.profile.label" />
                <VSpacer small />
                <InputText fieldValue={project.label ?? ``} onChange={(value: any) => updateName(value)} />
              </>
            ) : (
              <Grid item>
                <Text variant="label" intlId="projects.settings.general.profile.label" />
                <VSpacer small />
                <Text style={{ paddingBottom: `8px` }}>{project.label ?? ``}</Text>
              </Grid>
            )}

            <VSpacer medium />

            {canUpdateProject ? (
              <>
                <Text variant="label" intlId="projects.settings.general.profile.description" />
                <VSpacer small />
                <InputText
                  fieldValue={project.description ?? ``}
                  multiline
                  rowsMin={2}
                  rowsMax={3}
                  placeholderIntlId="projects.settings.general.profile.description_ph"
                  onChange={(value) => updateDescription(value)}
                />
              </>
            ) : (
              <Grid item>
                <Text variant="label" intlId="projects.settings.general.profile.description" />
                <VSpacer small />
                {project.description ? (
                  <Text style={{ paddingBottom: `8px` }}>{project.label ?? ``}</Text>
                ) : (
                  <Text intlId="none" />
                )}
              </Grid>
            )}
            <VSpacer medium />

            {canUpdateProject ? (
              <>
                <Text variant="label" intlId="projects.settings.general.profile.location" />
                <VSpacer small />
                <InputLocation
                  fieldValue={location ?? undefined}
                  inputProps={{
                    styles: {},
                    size: `medium`,
                    variant: `outlined`,
                    placeholder: locationLabel !== `` ? locationLabel : intl.formatMessage({ id: `none` }),
                  }}
                  emptyIntlId="no_results"
                  fullWidth
                  onUpdate={(loc?: Location | null) => {
                    setLocation(loc);
                    setLocationLabel(loc?.label ?? ``);
                    updateProject({ location: loc });
                  }}
                />
              </>
            ) : (
              <Grid item>
                <Text variant="label" intlId="projects.settings.general.profile.location" />
                <VSpacer small />
                {locationLabel !== `` ? (
                  <Text style={{ paddingBottom: `8px` }}>{locationLabel}</Text>
                ) : (
                  <Text intlId="none" />
                )}
              </Grid>
            )}
            <VSpacer medium />

            <>
              <Text variant="label" intlId="projects.settings.general.profile.time_zone" />
              <VSpacer small />
              <TimeZoneSelector
                id="time-zone-select"
                disabled={!canUpdateProject}
                timeZone={timezone}
                onChangeTimeZone={(newTimeZone) => {
                  setTimezone(newTimeZone);
                  updateProject({ time_zone: newTimeZone });
                }}
              />
            </>
          </ContainerInputs>
        </Grid>

        <VSpacer medium />

        <Grid item>
          <Text variant="h6" intlId="projects.settings.general.functionalities" />
        </Grid>

        <VSpacer small />
        <Grid
          container
          style={{
            borderRadius: `10px`,
            border: `1px solid ${RoutalPalette.neutral10}`,
            maxWidth: `500px`,
            padding: `14px`,
          }}
        >
          <Grid container style={{ display: `flex`, flexDirection: `column` }}>
            <Grid item style={{ display: `flex` }}>
              <Checkbox
                checked={userCredentials?.subscription.test(`planner.geofences`) ? project.geo_fences_enabled : false}
                onChange={() => {
                  updateProject({ geo_fences_enabled: !project.geo_fences_enabled });
                }}
                disabled={!userCredentials?.subscription.test(`planner.geofences`) || !canUpdateProject}
              />

              <Text
                variant="body-large"
                style={{ marginLeft: `4px`, alignSelf: `center` }}
                intlId="projects.settings.general.functionalities.zones"
              />
            </Grid>
            <VSpacer small />
            <Text variant="label" intlId="projects.settings.general.functionalities.zones.text" />
            <VSpacer medium />
          </Grid>
          <Grid container style={{ display: `flex`, flexDirection: `column` }}>
            <Grid item style={{ display: `flex` }}>
              <Checkbox
                checked={userCredentials?.subscription.test(`planner.tasks`) ? project.tasks_enabled : false}
                onChange={() => {
                  updateProject({ tasks_enabled: !project.tasks_enabled });
                }}
                disabled={!userCredentials?.subscription.test(`planner.tasks`) || !canUpdateProject}
              />

              <Text
                variant="body-large"
                style={{ marginLeft: `4px`, alignSelf: `center` }}
                intlId="projects.settings.general.functionalities.tasks"
              />
            </Grid>
            <VSpacer small />
            <Text variant="label" intlId="projects.settings.general.functionalities.tasks.text" />
            <VSpacer medium />
          </Grid>
          <Grid container style={{ display: `flex`, flexDirection: `column` }}>
            <Grid item style={{ display: `flex` }}>
              <Checkbox
                checked={project.view.constraints.pickups}
                onChange={() => {
                  const temporalView = ProjectViewMapper.toRepository(project.view);
                  temporalView.constraints.pickups = !project.view.constraints.pickups;
                  updateProject({ view: ProjectViewMapper.toDomain(temporalView) });
                }}
                disabled={!canUpdateProject}
              />
              <Text
                variant="body-large"
                style={{ marginLeft: `4px`, alignSelf: `center` }}
                intlId="projects.settings.general.functionalities.pickups"
              />
            </Grid>
            <VSpacer small />
            <Text variant="label" intlId="projects.settings.general.functionalities.pickups.text" />
          </Grid>
        </Grid>
        <VSpacer medium />

        <Grid item>
          <Text variant="h6" intlId="projects.settings.general.traffic" />
        </Grid>
        <VSpacer small />
        <Grid
          container
          style={{
            borderRadius: `10px`,
            border: `1px solid ${RoutalPalette.neutral10}`,
            backgroundColor: RoutalPalette.primary40n,
            maxWidth: `500px`,
            padding: `14px`,
          }}
        >
          <Grid container style={{ display: `flex`, flexDirection: `row`, flexWrap: `nowrap`, alignItems: `center` }}>
            <Checkbox
              checked={project.traffic}
              onChange={() => {
                setTrafficDialogVisible(true);
              }}
              disabled={
                !canUpdateProject ||
                (organization?.subscription.status === `trialing` &&
                  !project.traffic &&
                  !userCredentials?.isStaff(asUser))
              }
            />
            <HSpacer medium />
            <Text>
              <FormattedMessage
                id="projects.settings.general.traffic.text"
                values={{
                  value: (
                    <Text
                      variant="body"
                      tagName="span"
                      style={{ color: RoutalPalette.primary80 }}
                      isBold
                      intlId="projects.settings.general.traffic.price"
                    />
                  ),
                }}
              />
            </Text>
          </Grid>
        </Grid>
        <RoutalDialog
          open={trafficDialogVisible}
          onCloseDialog={() => {
            setTrafficDialogVisible(false);
          }}
          dialogProps={{
            disableEscapeKeyDown: true,
          }}
          titleContent={<Text variant="h6" intlId="projects.settings.general.traffic.dialog.title" />}
          footerContent={
            <Grid container justifyContent="flex-end">
              <Button
                onClick={() => setTrafficDialogVisible(false)}
                variant="flat"
                styleType="secondary"
                intlId="settings.buttoncancel"
              />

              <HSpacer small />

              <Button
                style={{
                  backgroundColor: project.traffic === false ? RoutalPalette.primary40 : RoutalPalette.error.medium,
                }}
                disabled={
                  updateTrafficLoading ||
                  (organization?.subscription.status === `trialing` &&
                    !project.traffic &&
                    !userCredentials?.isStaff(asUser))
                }
                styleType="secondary"
                variant="default"
                onClick={() => {
                  setTrafficDialogVisible(false);
                  updateTraffic({
                    projectId: project.id,
                    traffic: !project.traffic,
                  });
                }}
                intlId={
                  project.traffic
                    ? `projects.settings.general.traffic.dialog.disable`
                    : `projects.settings.general.traffic.dialog.enable`
                }
              />
            </Grid>
          }
        >
          {!project.traffic ? (
            <Text>
              <FormattedMessage
                id="projects.settings.general.traffic.dialog.text.enable"
                values={{
                  value: (
                    <Text
                      variant="body"
                      tagName="span"
                      style={{ color: RoutalPalette.primary80 }}
                      isBold
                      intlId="projects.settings.general.traffic.price"
                    />
                  ),
                }}
              />
            </Text>
          ) : (
            <Text>
              <FormattedMessage
                id="projects.settings.general.traffic.dialog.text.disable"
                values={{
                  label: `projects.settings.general.traffic.text`,
                }}
              />
            </Text>
          )}

          <VSpacer small />

          <VSpacer medium />
        </RoutalDialog>

        <VSpacer medium />

        <Grid item>
          <Text variant="h6" intlId="projects.settings.general.units" />
        </Grid>

        <VSpacer small />

        <Grid
          container
          direction="column"
          style={{
            borderRadius: `10px`,
            border: `1px solid ${RoutalPalette.neutral10}`,
            maxWidth: `500px`,
            padding: `14px`,
          }}
        >
          {units?.distance && (
            <>
              <Grid item>
                <Text variant="label" intlId="distance" />
                <VSpacer small />
                <RoutalSelect
                  id="select-distance"
                  containerStyle={{
                    width: `360px`,
                  }}
                  disabled={!canUpdateProject}
                  popoverProps={{
                    options: settingsDistance.map(
                      (setting: { value: DistanceType; label: string }): RoutalSelectMultiSelectOption => ({
                        key: setting.value,
                        listItemStyles: {
                          minWidth: `360px`,
                        },
                        content: <Text style={{ textTransform: `lowercase` }}>{setting.label}</Text>,
                        onClick: () => {
                          units.distance = setting.value;
                          setUnits(ProjectUnits.create(units.getValue()));
                          updateProject({ units: units });
                        },
                      })
                    ),
                  }}
                >
                  <Text style={{ width: `100%`, textAlign: `start`, cursor: `pointer` }}>{units.distance}</Text>
                </RoutalSelect>
              </Grid>
              <VSpacer small />
            </>
          )}

          {units?.weight && (
            <>
              <Grid item>
                <Text variant="label" intlId="weight" />
                <VSpacer small />
                <RoutalSelect
                  id="select-weight"
                  containerStyle={{
                    width: `360px`,
                  }}
                  disabled={!canUpdateProject}
                  popoverProps={{
                    options: settingsWeight.map(
                      (setting: { value: WeightType; label: string }): RoutalSelectMultiSelectOption => ({
                        key: setting.value,
                        listItemStyles: {
                          minWidth: `360px`,
                        },
                        content: <Text>{setting.label}</Text>,
                        onClick: () => {
                          units.weight = setting.value;
                          setUnits(ProjectUnits.create(units.getValue()));
                          updateProject({ units: units });
                        },
                      })
                    ),
                  }}
                >
                  <Text style={{ width: `100%`, textAlign: `start`, cursor: `pointer` }}>{units.weight}</Text>
                </RoutalSelect>
              </Grid>
              <VSpacer small />
            </>
          )}

          {units?.volume && (
            <>
              <Grid item>
                <Text variant="label" intlId="volume" />
                <VSpacer small />
                <RoutalSelect
                  id="select-weight"
                  containerStyle={{
                    width: `360px`,
                  }}
                  disabled={!canUpdateProject}
                  popoverProps={{
                    options: settingsVolume.map(
                      (setting: { value: VolumeType; label: string }): RoutalSelectMultiSelectOption => ({
                        key: setting.value,
                        listItemStyles: {
                          minWidth: `360px`,
                        },
                        content: <Text>{setting.label}</Text>,
                        onClick: () => {
                          units.volume = setting.value;
                          setUnits(ProjectUnits.create(units.getValue()));
                          updateProject({ units: units });
                        },
                      })
                    ),
                  }}
                >
                  <Text style={{ width: `100%`, textAlign: `start`, cursor: `pointer` }}>{units.volume}</Text>
                </RoutalSelect>
              </Grid>
              <VSpacer small />
            </>
          )}

          {units?.currency && (
            <>
              <Grid item>
                <Text variant="label" intlId="currency" />
                <VSpacer small />
                <RoutalSelect
                  id="select-weight"
                  containerStyle={{
                    width: `360px`,
                  }}
                  disabled={!canUpdateProject}
                  popoverProps={{
                    options: settingsCurrency.map(
                      (setting: { value: CurrencyTypes; label: string }): RoutalSelectMultiSelectOption => ({
                        key: setting.value,
                        listItemStyles: {
                          minWidth: `360px`,
                        },
                        content: <Text>{setting.label}</Text>,
                        onClick: () => {
                          units.currency = setting.value;
                          setUnits(ProjectUnits.create(units.getValue()));
                          updateProject({ units: units });
                        },
                      })
                    ),
                  }}
                >
                  <Text style={{ width: `100%`, textAlign: `start`, cursor: `pointer` }}>{units.currency}</Text>
                </RoutalSelect>
              </Grid>
              <VSpacer small />
            </>
          )}
        </Grid>

        <>
          {organization ? (
            <>
              {canUpdateProject ? (
                <>
                  <VSpacer medium />

                  <Grid item container direction="row" justifyContent="space-between" alignItems="center" wrap="nowrap">
                    <Grid item container wrap="nowrap">
                      <Text variant="h6" intlId="projects.settings.general.danger_zone.title" />
                    </Grid>
                  </Grid>

                  <VSpacer small />
                  <Grid
                    container
                    style={{
                      borderRadius: `10px`,
                      border: `1px solid ${RoutalPalette.error.medium}`,
                      maxWidth: `500px`,
                      padding: `14px`,
                    }}
                  >
                    <Grid item>
                      <Text variant="body-medium" intlId="projects.settings.general.danger_zone.description" />
                      <VSpacer small />
                    </Grid>

                    <Grid item container alignItems="center" spacing={1}>
                      <Grid item>
                        <Button
                          onClick={() => setDeleteDialogVisible(true)}
                          style={{
                            boxShadow: `unset`,
                            backgroundColor:
                              project.id === organization.default_project_id
                                ? `rgba(0, 0, 0, 0.12)`
                                : RoutalPalette.error.medium,
                          }}
                          styleType="secondary"
                          variant="default"
                          disabled={project.id === organization.default_project_id}
                          intlId="projects.settings.general.danger_zone.delete.title"
                        />
                      </Grid>
                      <Grid item>
                        {project.id === organization.default_project_id && (
                          <Text
                            variant="body"
                            style={{ cursor: `default`, color: RoutalPalette.error.medium, fontSize: `12px` }}
                            intlId="projects.settings.general.danger_zone.delete.default_project_disabled"
                          />
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                </>
              ) : null}
              <VSpacer large />
              <RoutalDialog
                open={!!deleteDialogVisible}
                onCloseDialog={() => {
                  setDeleteDialogVisible(false);
                }}
                dialogProps={{
                  disableEscapeKeyDown: true,
                }}
                titleContent={<Text variant="h6" intlId="projects.settings.general.danger_zone.delete.title" />}
                footerContent={
                  <Grid container justifyContent="flex-end">
                    <Button
                      onClick={() => setDeleteDialogVisible(false)}
                      variant="flat"
                      styleType="secondary"
                      intlId="settings.buttoncancel"
                    />

                    <HSpacer small />

                    <Button
                      disabled={
                        confirmDeleteOrganization === `` ||
                        (project.label ?? ``).toLowerCase() !== confirmDeleteOrganization.toLowerCase()
                      }
                      style={{
                        backgroundColor:
                          confirmDeleteOrganization === `` ||
                          (project.label ?? ``).toLowerCase() !== confirmDeleteOrganization.toLowerCase()
                            ? `rgba(0, 0, 0, 0.12)`
                            : RoutalPalette.error.medium,
                      }}
                      styleType="secondary"
                      variant="default"
                      onClick={() => {
                        deleteProject({ project });
                      }}
                      intlId="projects.settings.general.danger_zone.delete.title"
                    />
                  </Grid>
                }
              >
                <Text>
                  <FormattedMessage
                    id="projects.settings.general.danger_zone.delete.info"
                    values={{
                      label: project.label ?? ``,
                    }}
                  />
                </Text>

                <VSpacer small />

                <VSpacer medium />

                <Text isBold intlId="projects.settings.general.danger_zone.delete.label" />

                <VSpacer small />

                <InputText
                  id="delete_project_confirmation_input"
                  placeholder={intl.formatMessage({
                    id: `projects.settings.general.danger_zone.delete.label_ph`,
                  })}
                  fieldValue={confirmDeleteOrganization}
                  onChange={(value: any) => setConfirmDeleteOrganization(value)}
                />
              </RoutalDialog>
            </>
          ) : null}
        </>

        <VSpacer />
        <VSpacer />
      </Grid>
    </div>
  );
};
